import { getDoc, doc } from 'firebase/firestore'
import { fetchAndCacheImage } from '@helpers/imageHelper'
import { logger } from '@helpers/logger' // Updated to use the new log structure

// Cache to hold ongoing requests
const requestCache = new Map()

// Function to fetch teams data
export const getTeamsData = async (db, sport, league, strCurrentSeason, teamsData, setTeamsData) => {
  const params = { sport, league, strCurrentSeason } // Define parameters for logging

  const logComplete = logger.serviceRequest('getTeamsData', params) // Log service request

  const cacheKey = `${sport}-${league}-${strCurrentSeason}` // Unique key for caching requests

  // Check if there's already a pending promise for this request
  if (requestCache.has(cacheKey)) {
    logComplete(true) // Log as cached request
    return requestCache.get(cacheKey) // Return the pending promise
  }

  const requestPromise = (async () => {
    try {
      // Check if teams data already exists in teamsData
      if (teamsData.sports[sport] && teamsData.sports[sport][league] && teamsData.sports[sport][league].teams) {
        logComplete(true) // Log as cached request
        return {
          ...teamsData.sports[sport][league].teams,
        }
      }

      // Fetch teams from teams/teams document
      const teamsDocRef = doc(db, 'sports', sport, 'leagues', league, 'seasons', strCurrentSeason, 'teams', 'teams')
      const teamsDocSnapshot = await getDoc(teamsDocRef)

      if (!teamsDocSnapshot.exists()) {
        throw new Error(`Teams data not found for sport: ${sport}, league: ${league}, season: ${strCurrentSeason}`)
      }

      const teams = teamsDocSnapshot.data()

      Object.values(teams).map((team) => {
        team.strTeamOriginal = team.strTeam
        team.strTeam = team?.strTeamDisplayName || team.strTeam
      })

      // Fetch images for team badges and logos using the imageHelper functions
      await Promise.all(Object.values(teams).flatMap((team) => [fetchAndCacheImage(team.strBadge), fetchAndCacheImage(team.strLogo)]))

      // Update teams data state
      if (setTeamsData) {
        setTeamsData((prevTeamsData) => {
          const newTeamsData = { ...prevTeamsData }
          if (!newTeamsData.sports[sport]) {
            newTeamsData.sports[sport] = {}
          }
          if (!newTeamsData.sports[sport][league]) {
            newTeamsData.sports[sport][league] = {}
          }
          newTeamsData.sports[sport][league].teams = teams
          return newTeamsData
        })
      }

      logComplete() // Call the completion logging function
      return teams
    } catch (error) {
      logger.error('getTeamsData', error)
      logComplete() // Call the completion logging function even on error
      return {
        teams: [],
      }
    } finally {
      // Clean up the cache
      requestCache.delete(cacheKey)
    }
  })()

  // Cache the promise
  requestCache.set(cacheKey, requestPromise)
  return requestPromise // Return the promise
}
